"use client";

import * as React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  num: "01",
  title: "Credit Repair",
  description: "We initiate the process by identifying and disputing inaccuracies on your personal and business reports. Our strategic approach ensures a clean slate, laying the groundwork for high-limit credit acquisition and financial stability."
}, {
  num: "02",
  title: "Profile Optimization",
  description: "Our consultants engineer your credit profile to meet strict lender algorithms. We focus on debt-to-income ratios, utilization management, and seasoning your accounts to present a low-risk, high-value profile to major institutions."
}, {
  num: "03",
  title: "Scaling & Funding",
  description: "Once optimized, we leverage your enhanced credit to secure substantial capital. Whether for business expansion or personal wealth building, we navigate the funding landscape to get you the stacks of cash and credit you deserve."
}];
function StepItem({
  step,
  isLast,
  index
}: {
  step: (typeof steps)[0];
  isLast: boolean;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <div ref={ref} data-index={index} className={`flex gap-6 md:gap-12 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      <div className="flex flex-col items-center shrink-0 w-16 md:w-24">
        <div className="text-5xl md:text-7xl font-default font-bold text-primary leading-none tracking-tighter drop-shadow-[0_0_15px_rgba(34,197,94,0.3)]">
          {step.num}
        </div>
        {!isLast && <div className="w-px grow border-l-2 border-dashed border-primary/30 my-6 min-h-[4rem] md:min-h-[6rem]" />}
      </div>

      {}
      <div className={`pt-2 ${!isLast ? "pb-12 md:pb-20" : ""}`}>
        <h3 className="text-2xl md:text-3xl font-default font-semibold text-foreground mb-4">
          {step.title}
        </h3>
        <p className="text-muted-foreground text-lg leading-relaxed max-w-2xl font-sans">
          {step.description}
        </p>
      </div>
    </div>;
}
export default function OurDesignProcess() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="223290" id="the-path-to-capital" className="bg-background text-foreground py-20 md:py-32 overflow-hidden relative">
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/graphy.png')]" />
      
      <div className="container mx-auto px-4 md:px-6 max-w-5xl relative z-10">
        {}
        <div ref={headerRef} className={`mb-16 md:mb-24 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-5xl font-default font-bold text-foreground mb-6 tracking-tight">
            The Path to Capital
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl font-sans">
            A high-performance roadmap to scaling your personal and business credit, 
            designed to secure the funding your enterprise deserves.
          </p>
        </div>

        {}
        <div className="flex flex-col">
          {steps.map((step, i) => <StepItem key={step.num} step={step} index={i} isLast={i === steps.length - 1} />)}
        </div>

        {}
        <div className="mt-16 md:mt-24 ml-22 md:ml-36">
          <Button asChild size="lg" className="rounded-md px-8 bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/20 transition-all hover:-translate-y-1">
            <Link to="/ContactPage">
              Start Your Scaling Journey
              <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
          <p className="mt-6 text-sm text-muted-foreground/60 font-mono tracking-wider uppercase">
            Timeline: 30-90 Days to Funding
          </p>
        </div>
      </div>
    </section>;
}